import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plane } from 'lucide-react'
import { useAuth } from '@pilot-hub/auth'
import { flightApi } from '@pilot-hub/api-client'
import { lokaleFluege } from '../utils/flugQueue'
import { useFlugQueue } from './FlugQueueHinweis'

/** Laufzeit als h:mm:ss bzw. mm:ss */
export function formatLaufzeit(start: string | null | undefined, jetzt: number = Date.now()): string {
  if (!start) return '–:––'
  const s = Math.max(0, Math.floor((jetzt - new Date(start).getTime()) / 1000))
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sek = s % 60
  const zz = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${zz(m)}:${zz(sek)}` : `${zz(m)}:${zz(sek)}`
}

/** Sekundentakt für laufende Anzeigen */
export function useSekundenTakt(aktiv: boolean): number {
  const [jetzt, setJetzt] = useState(() => Date.now())
  useEffect(() => {
    if (!aktiv) return
    setJetzt(Date.now())
    const t = setInterval(() => setJetzt(Date.now()), 1000)
    return () => clearInterval(t)
  }, [aktiv])
  return jetzt
}

/**
 * Flugstatus auf allen Seiten (modul-lokal): „Flug läuft 12:34“ als Chip, Tippen öffnet „Flug erfassen“.
 * variant 'kopf' = Kopfzeile (ab sm), 'streifen' = schmaler Streifen unter der Kopfzeile (Handy).
 */
export function FlugStatusChip({ variant }: { variant: 'kopf' | 'streifen' }) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const queue = useFlugQueue()
  const [start, setStart] = useState<string | null>(null)
  const [anzahl, setAnzahl] = useState(0)

  useEffect(() => {
    if (!user?.id) return
    let weg = false
    const laden = () => {
      if (document.visibilityState === 'hidden') return
      flightApi.aktive().then(r => {
        if (weg) return
        const l = ((r.data as { startzeit: string }[]) ?? []).map(f => f.startzeit).sort()
        setAnzahl(l.length); setStart(l[0] ?? null)
      }).catch(() => { /* letzten Stand behalten */ })
    }
    laden()
    const t = setInterval(laden, 15000)
    return () => { weg = true; clearInterval(t) }
  }, [user?.id])

  const lokal = user?.id ? lokaleFluege(user.id).filter(l => !l.beendet) : []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const gesamt = anzahl + lokal.length
  const frueh = [start, ...lokal.map(l => l.startzeit)].filter((x): x is string => !!x).sort()[0] ?? null
  void queue
  const jetzt = useSekundenTakt(gesamt > 0)
  if (gesamt === 0) return null

  const text = `Flug läuft ${formatLaufzeit(frueh, jetzt)}${gesamt > 1 ? ` (${gesamt})` : ''}`
  const inhalt = (
    <>
      <span className="relative flex h-2.5 w-2.5 shrink-0"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/70" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" /></span>
      <Plane className="h-4 w-4 shrink-0" />
      <span className="tabular-nums">{text}</span>
    </>
  )
  if (variant === 'streifen') {
    return (
      <button type="button" onClick={() => navigate('/flug-erfassen')} aria-label={`${text} – zu Flug erfassen`}
        className="flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl border border-emerald-400/40 bg-emerald-500/15 px-3 text-sm font-semibold text-emerald-200 xl:hidden">
        {inhalt}
      </button>
    )
  }
  return (
    <button type="button" onClick={() => navigate('/flug-erfassen')} aria-label={`${text} – zu Flug erfassen`}
      className="hidden min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-emerald-400/40 bg-emerald-500/15 px-3 text-sm font-semibold text-emerald-200 xl:inline-flex">
      {inhalt}
    </button>
  )
}
